import { Component, type ErrorInfo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ApiError } from "@/lib/api";

type Props = { children: ReactNode };
type State = { error: Error | null };

function GenericErrorPanel() {
  const { t } = useTranslation();
  return (
    <div role="alert" className="mx-auto max-w-md p-8 text-center">
      <h2 className="text-2xl font-semibold tracking-tight">{t("error.generic")}</h2>
      <p className="mt-2 text-sm text-muted-foreground">{t("error.genericDescription")}</p>
    </div>
  );
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, _info: ErrorInfo): void {
    if (error instanceof ApiError) {
      toast.error(error.message);
      this.setState({ error: null });
    }
  }

  override render(): ReactNode {
    if (this.state.error && !(this.state.error instanceof ApiError)) {
      return <GenericErrorPanel />;
    }
    return this.props.children;
  }
}
